import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStudyRoom } from '../contexts/StudyRoomContext';
import PeerManager from './PeerManager';

const PersonIcon = ({ size = 40 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
);

const StudyRoomCard = ({ room, isEntered, onEnter, onLeave }) => {
    const { t } = useTranslation();
    const { setStudyStatus, cameraOn, liveStream, toggleCamera, switchCamera, facingMode } = useStudyRoom();
    const [elapsed, setElapsed] = useState('');
    const [remoteSnapshotUrl, setRemoteSnapshotUrl] = useState(null);
    const [remoteCameraOn, setRemoteCameraOn] = useState(false);
    const [peerConnected, setPeerConnected] = useState(false);
    const peerManagerRef = useRef(null);
    const liveVideoRef = useRef(null);

    const [roomElapsed, setRoomElapsed] = useState('');

    // 공부 세션 경과 시간
    useEffect(() => {
        if (!room.isStudying || !room.startTime) {
            setElapsed('');
            return;
        }

        const updateElapsed = () => {
            const diff = Math.floor((Date.now() - new Date(room.startTime).getTime()) / 1000);
            const hours = Math.floor(diff / 3600);
            const minutes = Math.floor((diff % 3600) / 60);
            const seconds = diff % 60;
            if (hours > 0) {
                setElapsed(`${hours}${t('studyroom.hour') || 'h'} ${minutes}${t('studyroom.min') || 'm'}`);
            } else {
                setElapsed(`${minutes}${t('studyroom.min') || 'm'} ${seconds}${t('studyroom.sec') || 's'}`);
            }
        };
        updateElapsed();
        const interval = setInterval(updateElapsed, 1000);
        return () => clearInterval(interval);
    }, [room.isStudying, room.startTime, t]);

    // 방 개설 경과 시간
    useEffect(() => {
        if (!room.openedAt) {
            setRoomElapsed('');
            return;
        }

        const updateRoomElapsed = () => {
            const diff = Math.floor((Date.now() - new Date(room.openedAt).getTime()) / 1000);
            const hours = Math.floor(diff / 3600);
            const minutes = Math.floor((diff % 3600) / 60);
            const seconds = diff % 60;
            if (hours > 0) {
                setRoomElapsed(`${hours}${t('studyroom.hour') || 'h'} ${String(minutes).padStart(2, '0')}${t('studyroom.min') || 'm'}`);
            } else {
                setRoomElapsed(`${minutes}${t('studyroom.min') || 'm'} ${String(seconds).padStart(2, '0')}${t('studyroom.sec') || 's'}`);
            }
        };
        updateRoomElapsed();
        const interval = setInterval(updateRoomElapsed, 1000);
        return () => clearInterval(interval);
    }, [room.openedAt, t]);

    // Mine 카드: Context의 liveStream을 video 엘리먼트에 연결
    useEffect(() => {
        if (liveVideoRef.current && liveStream) {
            liveVideoRef.current.srcObject = liveStream;
        }
    }, [liveStream, cameraOn]);

    // PeerManager 콜백: P2P 스냅샷 수신
    const handlePeerSnapshot = useCallback((blob) => {
        const url = URL.createObjectURL(blob);
        setRemoteSnapshotUrl(prev => {
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return url;
        });
    }, []);

    // PeerManager 콜백: P2P 상태 메시지 수신 → 카메라 상태 반영
    const handlePeerStatusMessage = useCallback((msg) => {
        if (msg.cameraOn !== undefined) {
            setRemoteCameraOn(msg.cameraOn);
            if (!msg.cameraOn) {
                setRemoteSnapshotUrl(prev => {
                    if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
                    return null;
                });
            }
        }
    }, []);

    // PeerManager 콜백: P2P 연결 상태 변경
    const handlePeerConnectionChange = useCallback((state) => {
        setPeerConnected(state === 'connected');
    }, []);

    // Mine 카드: studyStatus를 Context에 동기화
    useEffect(() => {
        if (room.isMine) {
            setStudyStatus({
                subject: room.subject,
                subjectColor: room.subjectColor,
                startTime: room.startTime,
                isStudying: room.isStudying,
                cameraOn
            });
        }
    }, [room.isMine, room.subject, room.subjectColor, room.startTime, room.isStudying, cameraOn, setStudyStatus]);

    // P2P 연결 해제 시 스냅샷 정리
    useEffect(() => {
        if (!isEntered) {
            setRemoteSnapshotUrl(null);
            setPeerConnected(false);
            setRemoteCameraOn(false);
        }
    }, [isEntered]);

    // Mine 카드 또는 입장 카드 → 세로 확장 뷰
    if (room.isMine || isEntered) {
        const showRemoteSnapshot = !room.isMine && remoteCameraOn && remoteSnapshotUrl;

        return (
            <div className={`studyroom-card studyroom-card-entered ${room.isMine ? 'studyroom-card-mine' : ''}`}>
                <div className="studyroom-card-camera">
                    {room.isMine && cameraOn ? (
                        <video
                            ref={liveVideoRef}
                            autoPlay
                            playsInline
                            muted
                            className="studyroom-snapshot"
                        />
                    ) : showRemoteSnapshot ? (
                        <img src={remoteSnapshotUrl} alt="camera" className="studyroom-snapshot" />
                    ) : (
                        <div className="studyroom-card-avatar-large">
                            <PersonIcon size={60} />
                        </div>
                    )}
                </div>

                <div className="studyroom-card-details">
                    <div className="studyroom-card-username">
                        {room.username}
                        {room.isMine && <span className="studyroom-mine-badge">{t('studyroom.me') || 'ME'}</span>}
                    </div>
                    <div className="studyroom-card-divider"></div>

                    {room.isStudying ? (
                        <>
                            <div className="studyroom-card-status studyroom-status-studying">
                                {t('studyroom.studying')}
                            </div>
                            <div className="studyroom-card-subject">
                                <span className="studyroom-subject-dot" style={{ backgroundColor: room.subjectColor }}></span>
                                {room.subject}
                            </div>
                            {room.startTime && (
                                <div className="studyroom-card-time">
                                    {t('studyroom.startTime')}: {new Date(room.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            )}
                            <div className="studyroom-card-elapsed">
                                {t('studyroom.elapsed')}: {elapsed}
                            </div>
                        </>
                    ) : (
                        <div className="studyroom-card-status studyroom-status-online">
                            {t('studyroom.online')}
                        </div>
                    )}
                    {roomElapsed && (
                        <div className="studyroom-card-room-elapsed">
                            {t('studyroom.roomElapsed')}: {roomElapsed}
                        </div>
                    )}
                </div>

                {room.isMine ? (
                    <div className="studyroom-card-mine-footer">
                        <div className="camera-toggle-container">
                            <button
                                className={`camera-toggle-btn ${cameraOn ? 'camera-on' : 'camera-off'}`}
                                onClick={toggleCamera}
                            >
                                {cameraOn ? t('studyroom.camera.off') : t('studyroom.camera.on')}
                            </button>
                            {cameraOn && (
                                <button
                                    className="camera-switch-btn"
                                    onClick={switchCamera}
                                    title={facingMode === 'user' ? t('studyroom.camera.rear') : t('studyroom.camera.front')}
                                >
                                    &#x1F504;
                                </button>
                            )}
                        </div>
                        {room.observers && room.observers.length > 0 && (
                            <div className="studyroom-observers">
                                <span className="studyroom-observers-label">
                                    {t('studyroom.observers', { count: room.observers.length })}:
                                </span>
                                <span className="studyroom-observers-list">
                                    {room.observers.map(o => o.username).join(', ')}
                                </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="studyroom-card-observer-footer">
                        <PeerManager
                            ref={peerManagerRef}
                            targetUserId={room.userId}
                            onStatusMessage={handlePeerStatusMessage}
                            onSnapshot={handlePeerSnapshot}
                            onConnectionChange={handlePeerConnectionChange}
                        />
                        <div className="studyroom-peer-status">
                            {peerConnected
                                ? <span className="studyroom-peer-connected">{t('studyroom.connected') || 'P2P 연결됨'}</span>
                                : <span className="studyroom-peer-connecting">{t('studyroom.connecting')}</span>
                            }
                        </div>
                        <button className="studyroom-leave-btn" onClick={onLeave}>
                            {t('studyroom.leave')}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // 타인 카드 - 리스트 뷰 (입장 전)
    return (
        <div className="studyroom-card studyroom-card-list">
            <div className="studyroom-card-avatar">
                <PersonIcon size={28} />
            </div>
            <div className="studyroom-card-info">
                <div className="studyroom-card-username">{room.username}</div>
                {room.isStudying ? (
                    <>
                        <div className="studyroom-card-status studyroom-status-studying">
                            {t('studyroom.studying')}
                        </div>
                        <div className="studyroom-card-subject">
                            <span className="studyroom-subject-dot" style={{ backgroundColor: room.subjectColor }}></span>
                            {room.subject}
                        </div>
                        <div className="studyroom-card-time">
                            {t('studyroom.startTime')}: {new Date(room.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </>
                ) : (
                    <div className="studyroom-card-status studyroom-status-online">
                        {t('studyroom.online')}
                    </div>
                )}
            </div>
            <button className="studyroom-enter-btn" onClick={onEnter}>
                {t('studyroom.enter')}
            </button>
        </div>
    );
};

export default StudyRoomCard;
